import React, { useEffect, useState } from 'react';
import { Box, Collapse, AlertTitle, Alert, Button, Typography } from '@mui/material';
import AppContainer from '../components/layout/AppContainer';
import SetupForm from '../components/forms/SetupForm';

import StepperContainer, { ISteps } from '../components/layout/StepperContainer';
import CardContainer from '../components/layout/CardContainer';
import DesignForm from '../components/forms/DesignForm';
import { IConfigurationState, LoadingSpinnerType, useConfigurationStore } from '../stores/ConfigStore';
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useTheme,
} from "@mui/material";
import { useThemeStore } from '../stores/ThemeStore';
import QRCode from '../components/shared/QRCodeCard';
import { useTenantStore } from '../stores/TenantStore';
import SetupResult from '../components/setup/SetupResult';

const steps: ISteps[] = [
  { title: 'Schnittstelle einrichten', step: 'Schnittstelle einrichten', optional: false, tooltip: "Diese Eingaben sollten durch einen Casablanca Techniker vorgenommen werden" },
  { title: 'Design anpassen', step: 'Design anpassen', optional: true },
  { title: 'E-Display Bereit', step: 'Fertig', optional: false },
];

const Setup = () => {
  const theme = useTheme()
  const { setPrimaryColor } = useThemeStore()
  let navigate = useNavigate();

  const { setIsRequestPending } = useTenantStore();
  setIsRequestPending(false);

  const [activeStep, setActiveStep] = useState(0);
  const {
    configuration,
    dispatchConfiguration,
  } = useConfigurationStore();
  const [displayUrl, setDisplayUrl] = useState("");
  const [isSetupSuccessful, setIsSetupSuccessful] = useState(false);

  /* Legacy Code for befilling form with parameters*/
  const [searchParams] = useSearchParams();

  useEffect(() => {
    searchParams.forEach((value, key) => {
      switch (key) {
        case 'tenantid':
          dispatchConfiguration({ type: 'SET_TENANT_ID', payload: decodeURIComponent(value) });
          break;
        case 'deviceName':
          dispatchConfiguration({ type: 'SET_DEVICE_NAME', payload: decodeURIComponent(value) });
          break;
        case 'advertisementUrl':
          dispatchConfiguration({ type: 'SET_PLACEHOLDER_URL', payload: decodeURIComponent(value) });
          break;
        default:
          console.warn(`Unhandled configuration key: ${key}`);
      }
    })
  }, [searchParams]);

  /* States and OnChange for SetupForm */
  const [tenantIdValue, setTenantIdValue] = useState(
    configuration.setup.tenantId || "AT_12324_HOTELA"
  );
  const [deviceNameValue, setDeviceNameValue] = useState(
    configuration.setup.deviceName || "Ipad"
  );
  const [placeholderUrlValue, setPlaceholderUrlValue] = useState(
    configuration.setup.placeholderUrl
  );

  /* States and OnChange for DesignForm */
  const [companyLogoUrlValue, setCompanyLogoUrlValue] = useState(
    configuration.design.companyLogoUrl
  );
  const [companyNameValue, setCompanyNameValue] = useState(
    configuration.design.companyName
  );
  const [showLogoValue, setShowLogoValue] = useState(
    configuration.design.showLogo
  );
  const [loadingSpinnerTypeValue, setLoadingSpinnerTypeValue] = useState(
    configuration.design.loadingSpinnerType
  );
  const [companyHexColorValue, setCompanyHexColorValue] = useState(
    configuration.design.companyHexColor || theme.palette.primary.main
  );
  const [openLinksInModalValue, setOpenLinksInModalValue] = useState(
    configuration.design.openLinksInModal
  );

  /* Update the Form values if the context changes */
  useEffect(() => {
    setTenantIdValue(configuration.setup.tenantId);
    setDeviceNameValue(configuration.setup.deviceName);
    setPlaceholderUrlValue(configuration.setup.placeholderUrl);
    setCompanyLogoUrlValue(configuration.design.companyLogoUrl);
    setCompanyNameValue(configuration.design.companyName);
    setShowLogoValue(configuration.design.showLogo);
    setLoadingSpinnerTypeValue(configuration.design.loadingSpinnerType);
    setCompanyHexColorValue(configuration.design.companyHexColor);
    setOpenLinksInModalValue(configuration.design.openLinksInModal);
  }, [configuration]);

  const getValidColor = (value: string) => {
    const isValid = /^#([0-9A-F]{3}){1,2}$/i.test(value);
    if (!isValid) {
      return '#1976d2'
    } else {
      return value
    }
  };

  const handleSubmit = () => {
    dispatchConfiguration({ type: "SET_TENANT_ID", payload: tenantIdValue });
    dispatchConfiguration({ type: "SET_DEVICE_NAME", payload: deviceNameValue });
    dispatchConfiguration({ type: "SET_PLACEHOLDER_URL", payload: placeholderUrlValue.convertToValidUrl() });
    dispatchConfiguration({ type: "SET_COMPANY_LOGO_URL", payload: companyLogoUrlValue });
    dispatchConfiguration({ type: "SET_COMPANY_NAME", payload: companyNameValue });
    dispatchConfiguration({ type: "SET_SHOW_LOGO", payload: showLogoValue });
    dispatchConfiguration({ type: "SET_LOADING_SPINNER_TYPE", payload: loadingSpinnerTypeValue });
    dispatchConfiguration({ type: "SET_COMPANY_HEX_COLOR", payload: getValidColor(companyHexColorValue) });
    dispatchConfiguration({ type: "SET_OPEN_LINKS_IN_MODAL", payload: openLinksInModalValue });
    setPrimaryColor(getValidColor(companyHexColorValue))
    navigate(displayUrl);
  };


  useEffect(() => {
    const setup = {
      tenantId: tenantIdValue,
      deviceName: deviceNameValue,
      placeholderUrl: placeholderUrlValue.convertToValidUrl()
    };

    const design = {
      companyLogoUrl: companyLogoUrlValue,
      companyName: companyNameValue,
      showLogo: showLogoValue,
      loadingSpinnerType: loadingSpinnerTypeValue,
      companyHexColor: getValidColor(companyHexColorValue),
      openLinksInModal: openLinksInModalValue
    };
    const url = buildDisplayURL({ setup, design });
    setDisplayUrl(url);
  }, [tenantIdValue, deviceNameValue, placeholderUrlValue, companyLogoUrlValue, companyNameValue, showLogoValue, loadingSpinnerTypeValue, companyHexColorValue, openLinksInModalValue]);

  useEffect(() => {
    setIsSetupSuccessful(
      tenantIdValue !== "AT_12324_HOTELA" &&
      tenantIdValue !== "" &&
      tenantIdValue !== null &&
      deviceNameValue !== "" &&
      deviceNameValue !== null
    );
  }, [tenantIdValue, deviceNameValue]);

  const StepContent = ({ stepIndex }: { stepIndex: number }) => {
    switch (stepIndex) {
      case 0:
        return <SetupForm
          tenantIdValue={tenantIdValue}
          deviceNameValue={deviceNameValue}
          placeholderUrlValue={placeholderUrlValue}
          setTenantIdValue={setTenantIdValue}
          setDeviceNameValue={setDeviceNameValue}
          setPlaceholderUrlValue={setPlaceholderUrlValue}
        />;
      case 1:
        return <DesignForm
          companyLogoUrlValue={companyLogoUrlValue}
          companyNameValue={companyNameValue}
          showLogoValue={showLogoValue}
          loadingSpinnerTypeValue={loadingSpinnerTypeValue}
          placeholderUrlValue={placeholderUrlValue}
          companyHexColorValue={companyHexColorValue}
          openLinksinModalValue={openLinksInModalValue}
          setCompanyLogoUrlValue={setCompanyLogoUrlValue}
          setCompanyNameValue={setCompanyNameValue}
          setShowLogoValue={setShowLogoValue}
          setLoadingSpinnerTypeValue={setLoadingSpinnerTypeValue}
          setCompanyHexColorValue={setCompanyHexColorValue}
          setOpenLinksInModal={setOpenLinksInModalValue}
        />
      case 2:
        return <SetupResult
          isSetupSuccessful={isSetupSuccessful}
          displayUrl={displayUrl}
          onGoBack={() => setActiveStep(0)}
        />
      default:
        return null;
    }
  };


  return (
    <AppContainer>
      <StepperContainer
        steps={steps}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        onSubmitForm={handleSubmit}
        isSetupSuccessful={isSetupSuccessful}>
        <CardContainer title={steps[activeStep].title} tooltip={steps[activeStep].tooltip} minHeight="40rem">
          <StepContent stepIndex={activeStep} />
        </CardContainer>
      </StepperContainer>
    </AppContainer>
  );
};

export default Setup;

export const buildDisplayURL = ({ setup, design }: IConfigurationState
) => {
  const queryParams = new URLSearchParams();

  if (setup.placeholderUrl !== undefined) {
    queryParams.set('placeholderUrl', setup.placeholderUrl);
  }
  if (design.companyLogoUrl !== undefined) {
    queryParams.set('companyLogoUrl', design.companyLogoUrl);
  }
  if (design.companyName !== undefined) {
    queryParams.set('companyName', design.companyName);
  }
  if (design.showLogo !== undefined) {
    queryParams.set('showLogo', design.showLogo.toString());
  }
  if (design.loadingSpinnerType !== undefined) {
    queryParams.set('loadingSpinnerType', design.loadingSpinnerType);
  }
  if (design.companyHexColor !== undefined) {
    queryParams.set('companyHexColor', design.companyHexColor);
  }
  if (design.openLinksInModal !== undefined) {
    queryParams.set('openLinksInModal', design.openLinksInModal.toString());
  }

  let url = "/";

  // Add tenantId and deviceName to the URL path
  if (setup.tenantId) url += `${encodeURIComponent(setup.tenantId)}/`;
  if (setup.deviceName) url += `${encodeURIComponent(setup.deviceName)}/`;

  // Append query parameters if any
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  return url;
};